import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Drawer,
} from "@mui/material";
import {
  EditNote,
  CheckCircleOutline,
  SmartToy,
  FindInPage,
  PersonOutline,
  ForumOutlined,
  ImageOutlined,
  MenuBookOutlined,
  GTranslate,
  BookmarkBorderOutlined,
  AutoGraphOutlined,
  WorkspacePremiumOutlined,
  SettingsOutlined,
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  Login as LoginOutlined,
} from "@mui/icons-material";

interface SidebarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  darkMode: boolean;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  userName: string | null | undefined;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const menuItems = [
  { id: "paraphraser", label: "Text Rewriter", icon: <EditNote /> },
  { id: "grammar", label: "Grammar Check", icon: <CheckCircleOutline /> },
  { id: "detector", label: "AI Detection", icon: <SmartToy /> },
  { id: "plagiarism", label: "Plagiarism Scan", icon: <FindInPage /> },
  { id: "humanizer", label: "Text Humanizer", icon: <PersonOutline /> },
  { id: "chat", label: "AI Assistant", icon: <ForumOutlined /> },
  { id: "image-generator", label: "Image Creator", icon: <ImageOutlined /> },
  { id: "summarizer", label: "Smart Summary", icon: <MenuBookOutlined /> },
  { id: "translate", label: "Language Bridge", icon: <GTranslate /> },
  { id: "citation", label: "Citation Tool", icon: <BookmarkBorderOutlined /> },
  { id: "flow", label: "Smart Flow", icon: <AutoGraphOutlined /> },
];

export default function Sidebar(props: SidebarProps): JSX.Element {
  const {
    currentPage,
    onPageChange,
    darkMode,
    collapsed,
    setCollapsed,
    userName,
    isMobile,
    mobileOpen,
    onMobileClose,
  } = props;

  const content = (
    <Box
      sx={{
        width: isMobile ? 280 : collapsed ? 80 : 280,
        height: "100%",
        bgcolor: darkMode ? "#030303" : "#ffffff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Brand Logo & Toggle Header */}
      <Box
        sx={{
          py: collapsed && !isMobile ? 2 : 0,
          height: collapsed && !isMobile ? "auto" : 80,
          px: 2.5,
          display: "flex",
          flexDirection: collapsed && !isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed && !isMobile ? "center" : "space-between",
          gap: collapsed && !isMobile ? 2 : 0,
          borderBottom:
            collapsed && !isMobile
              ? "none"
              : `1px solid ${darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"}`,
        }}
      >
        {/* Toggle Button - Only show if not mobile */}
        {!isMobile && (
          <Tooltip
            title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            placement="right"
            arrow
          >
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              size="small"
              sx={{
                order: collapsed ? 1 : 2,
                color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                bgcolor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)",
                border: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                borderRadius: 2,
                p: 0.8,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.08)",
                  color: darkMode ? "white" : "black",
                  transform: "scale(1.05)",
                },
              }}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        )}

        {/* Logo Section */}
        <Box
          onClick={() => {
            onPageChange("home");
            if (isMobile && onMobileClose) onMobileClose();
          }}
          sx={{
            order: collapsed && !isMobile ? 2 : 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src="/images/logo-tab.png"
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: darkMode ? "white" : "#1e293b",
              fontSize: "1.25rem",
              letterSpacing: "-0.02em",
              display: collapsed && !isMobile ? "none" : "block",
              whiteSpace: "nowrap",
            }}
          >
            CoWrite
          </Typography>
        </Box>
      </Box>

      {/* Navigation List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          py: 2,
          px: 2,
          /* Hide scrollbar */
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none" /* Firefox */,
          msOverflowStyle: "none" /* IE and Edge */,
        }}
      >
        <List sx={{ gap: 0.5, display: "flex", flexDirection: "column" }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              sx={{ display: "block", mb: 0.5 }}
            >
              <Tooltip
                title={collapsed && !isMobile ? item.label : ""}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={() => {
                    onPageChange(item.id);
                    if (isMobile && onMobileClose) onMobileClose();
                  }}
                  selected={currentPage === item.id}
                  sx={{
                    borderRadius: 3,
                    minHeight: 48,
                    justifyContent:
                      collapsed && !isMobile ? "center" : "initial",
                    px: collapsed && !isMobile ? 1.5 : 2,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    bgcolor:
                      currentPage === item.id
                        ? darkMode
                          ? "rgba(59, 130, 246, 0.15)"
                          : "rgba(59, 130, 246, 0.08)"
                        : "transparent",
                    color:
                      currentPage === item.id
                        ? "#3b82f6"
                        : darkMode
                          ? "#94a3b8"
                          : "#64748b",
                    "&.Mui-selected": {
                      bgcolor: darkMode
                        ? "rgba(59, 130, 246, 0.15)"
                        : "rgba(59, 130, 246, 0.08)",
                      color: "#3b82f6",
                      "&:hover": {
                        bgcolor: darkMode
                          ? "rgba(59, 130, 246, 0.25)"
                          : "rgba(59, 130, 246, 0.15)",
                      },
                      "& .MuiListItemIcon-root": { color: "#3b82f6" },
                      "& .MuiListItemText-primary": {
                        color: "#3b82f6",
                        fontWeight: 700,
                      },
                    },
                    "&:hover": {
                      bgcolor: darkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed && !isMobile ? 0 : 2,
                      justifyContent: "center",
                      color:
                        currentPage === item.id
                          ? "#3b82f6"
                          : darkMode
                            ? "#94a3b8"
                            : "#64748b",
                      transition: "color 0.2s",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: {
                        fontWeight: currentPage === item.id ? 700 : 500,
                        whiteSpace: "nowrap",
                      },
                    }}
                    sx={{
                      opacity: collapsed && !isMobile ? 0 : 1,
                      display: collapsed && !isMobile ? "none" : "block",
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer / Upgrade Section */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          bgcolor: darkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
        }}
      >
        {!collapsed || isMobile ? (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                background: darkMode
                  ? "linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(124, 58, 237, 0.15) 100%)"
                  : "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(124, 58, 237, 0.05) 100%)",
                border: darkMode
                  ? "1px solid rgba(37, 99, 235, 0.3)"
                  : "1px solid rgba(37, 99, 235, 0.2)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 30px -10px rgba(59, 130, 246, 0.4)",
                },
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
              onClick={() => {
                onPageChange("premium");
                if (isMobile && onMobileClose) onMobileClose();
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                }}
              >
                <WorkspacePremiumOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: darkMode ? "white" : "#0f172a",
                    lineHeight: 1.2,
                  }}
                >
                  CoWrite Pro
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: darkMode
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.5)",
                    fontWeight: 500,
                  }}
                >
                  Unlock all features
                </Typography>
              </Box>
            </Paper>

            {userName ? (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<SettingsOutlined />}
                  onClick={() => {
                    onPageChange("settings");
                    if (isMobile && onMobileClose) onMobileClose();
                  }}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    color: darkMode ? "#94a3b8" : "#64748b",
                    "&:hover": {
                      bgcolor: darkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                      color: darkMode ? "white" : "black",
                    },
                  }}
                >
                  Settings
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  startIcon={<AccountCircle />}
                  onClick={() => {
                    onPageChange("profile");
                    if (isMobile && onMobileClose) onMobileClose();
                  }}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    color: darkMode ? "#94a3b8" : "#64748b",
                    "&:hover": {
                      bgcolor: darkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                      color: darkMode ? "white" : "black",
                    },
                  }}
                >
                  Profile
                </Button>
              </Stack>
            ) : (
              <Button
                fullWidth
                variant="contained"
                startIcon={<LoginOutlined />}
                onClick={() => {
                  onPageChange("signin");
                  if (isMobile && onMobileClose) onMobileClose();
                }}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                }}
              >
                Sign In
              </Button>
            )}
          </>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Tooltip title="Upgrade to Pro" placement="right" arrow>
              <IconButton
                onClick={() => onPageChange("premium")}
                sx={{
                  width: 44,
                  height: 44,
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%)",
                  color: "white",
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                  "&:hover": { transform: "scale(1.05)" },
                }}
              >
                <WorkspacePremiumOutlined fontSize="small" />
              </IconButton>
            </Tooltip>

            {userName ? (
              <>
                <Tooltip title="Settings" placement="right" arrow>
                  <IconButton
                    onClick={() => onPageChange("settings")}
                    sx={{ color: darkMode ? "#94a3b8" : "#64748b" }}
                  >
                    <SettingsOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Profile" placement="right" arrow>
                  <IconButton
                    onClick={() => onPageChange("profile")}
                    sx={{ color: darkMode ? "#94a3b8" : "#64748b" }}
                  >
                    <AccountCircle />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Sign In" placement="right" arrow>
                <IconButton
                  onClick={() => onPageChange("signin")}
                  sx={{
                    color: "white",
                    bgcolor: "primary.main",
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  <LoginOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );

  return isMobile ? (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: 280,
          bgcolor: darkMode ? "#030303" : "#ffffff",
          backgroundImage: "none",
          borderRight: "none",
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Box
      sx={{
        width: collapsed ? 80 : 280,
        height: "100vh",
        bgcolor: darkMode ? "#030303" : "#ffffff",
        borderRight: "1px solid",
        borderColor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1200,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
    >
      {content}
    </Box>
  );
}
